import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ShoppingBag, Users, Package, BarChart3, LayoutGrid, Lock, Truck, Megaphone, UsersRound, Settings, Cloud, ReceiptText, Wallet, Stethoscope,
  Inbox, MonitorSmartphone, ScanSearch, Maximize, Keyboard, Clock3, MonitorCog, CreditCard, Vault,
  ClipboardList, ArrowLeftRight,
  Landmark,
} from "lucide-react";
import PlanBanner from "@/components/PlanBanner";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Logo";
import { SyncPill } from "@/components/SyncPill";
import { LangSwitch } from "@/components/LangSwitch";
import { NotificationBell } from "@/components/NotificationBell";
import { isLow, vals } from "@/lib/format";
import { currentShift } from "@/lib/shift";
import { scopeAllows } from "@/lib/scope";
import { SHORTCUTS, closeAllWindows, openCustomerDisplay, openDrawerManual, touchActive, useHotkeys } from "@/lib/tpv";
import ShiftDialog from "@/components/tpv/ShiftDialog";
import DrawerKick from "@/components/tpv/DrawerKick";
import HelpDialog from "@/components/tpv/HelpDialog";
import { RibbonHeader, StatusBar } from "@/components/tpv/Ribbon";
import ClockButton from "@/components/ClockButton";

const FX_ROUTES = ["/exchange", "/expenses", "/vault", "/bank", "/finance", "/reports", "/team", "/settings", "/billing", "/more"];
export const showFor = (item, type, shop, role) => (!item.clinic || type === "clinic") && !(item.hide || []).includes(type) && (!item.only || item.only.includes(type)) && (type !== "exchange" || FX_ROUTES.includes(item.to)) && scopeAllows(shop, item.to) && (!item.owner || role === "owner");
const NAV = [
  { to: "/exchange", icon: ArrowLeftRight, label: "type_exchange", main: true, only: ["exchange"] },
  { to: "/sell", icon: ShoppingBag, label: "nav_sell", main: true },
  { to: "/clinic", icon: Stethoscope, label: "clinic", perm: "clinic", main: true, clinic: true },
  { to: "/tpv", icon: MonitorCog, label: "tpv_section", hide: ["clinic"] },
  { to: "/customers", icon: Users, label: "nav_customers", main: true, badge: "debtors" },
  { to: "/inventory", icon: Package, label: "nav_stock", main: true, badge: "low" },
  { to: "/sales", icon: ReceiptText, label: "sales_history" },
  { to: "/reports", icon: BarChart3, label: "nav_reports", perm: "reports", main: true },
  { to: "/purchases", icon: Truck, label: "purchases", perm: "purchases" },
  { to: "/expenses", icon: Wallet, label: "expenses", perm: "purchases" },
  { to: "/orders", icon: ClipboardList, label: "orders", perm: "purchases", hide: ["clinic"] },
  { to: "/vault", icon: Vault, label: "admin_vault", owner: true },
  { to: "/bank", icon: Landmark, label: "virtual_bank", owner: true, perm: "bank" },
  { to: "/marketing", icon: Megaphone, label: "marketing", perm: "marketing", hide: ["clinic", "pharmacy"] },
  { to: "/team", icon: UsersRound, label: "team", perm: "team" },
  { to: "/settings", icon: Settings, label: "settings" },
  { to: "/billing", icon: CreditCard, label: "billing", perm: "billing" },
  { to: "/more", icon: LayoutGrid, label: "modules" },
];

function useBadges() {
  const { view } = useApp();
  return {
    debtors: vals(view.customers).filter((c) => c.balance > 0).length,
    low: vals(view.products).filter((p) => !p.deleted && isLow(p, view.shop?.low_stock_default)).length,
  };
}

function Rail({ items }) {
  const { t } = useApp();
  const badges = useBadges();
  return (
    <aside className="hidden lg:flex fixed top-16 bottom-11 start-0 w-[104px] bg-[#0B2A5B] bg-[linear-gradient(180deg,#0F3C7A_0%,#0B2A5B_45%,#061436_100%)] text-white flex-col z-30 no-print overflow-y-auto no-scrollbar" data-testid="app-sidebar">
      {items.map(({ to, icon: Icon, label, badge }, i) => (
        <NavLink key={to} to={to} data-testid={`sidenav-${to.slice(1)}`} title={`Alt+${i + 1}`}
          className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 min-h-[76px] px-1 border-s-4 text-[11px] font-semibold text-center leading-tight transition-colors ${isActive ? "border-[#F28C28] bg-white/10 text-white" : "border-transparent text-white/70 hover:bg-white/5 hover:text-white"}`}>
          <Icon className="w-6 h-6" strokeWidth={1.75} />
          <span>{t(label)}</span>
          {i < 9 && <span className="absolute top-1.5 start-2 text-[9px] text-white/40" dir="ltr">{i + 1}</span>}
          {badge && badges[badge] > 0 && <span className={`absolute top-1.5 end-2 min-w-5 h-5 px-1 rounded-full text-[10px] font-bold grid place-items-center ${badge === "low" ? "bg-[#F0444B]" : "bg-[#E67E22]"}`} data-testid={`sidenav-badge-${badge}`}>{badges[badge]}</span>}
        </NavLink>
      ))}
    </aside>
  );
}

function BottomNav() {
  const { t, can, view, staff } = useApp();
  const items = [...NAV.filter((n) => n.main && (!n.perm || can(n.perm)) && showFor(n, view.shop?.type, view.shop, staff?.role)), NAV[NAV.length - 1]];
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#F0F0F0] text-[#333333] border-t border-[#CCCCCC] no-print">
      <div className="grid grid-flow-col auto-cols-fr">
        {items.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 h-[68px] text-[11px] font-semibold transition-colors ${isActive ? "text-[#0B2A5B]" : "text-[#555555]"}`}>
            {({ isActive }) => (
              <>
                <span className={`w-12 h-8 rounded grid place-items-center transition-colors border ${isActive ? "bg-[#FFF1E3] border-[#F28C28]" : "border-transparent"}`}><Icon className="w-5 h-5" /></span>
                {t(label)}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 15000); return () => clearInterval(id); }, []);
  return (
    <div dir="ltr" className="hidden md:block text-end leading-tight" data-testid="tpv-clock">
      <div className="text-base font-bold">{now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
      <div className="text-[10px] text-[#666666]">{now.toLocaleDateString("en-GB")}</div>
    </div>
  );
}

const IconBtn = ({ icon: Icon, label, onClick, testid, className = "" }) => (
  <button type="button" onClick={onClick} title={label} aria-label={label} data-testid={testid}
    className={`w-10 h-10 shrink-0 rounded border border-transparent hover:bg-white/15 hover:border-white/20 grid place-items-center transition-colors ${className}`}><Icon className="w-5 h-5" /></button>
);

function TerminalBar({ onShift, onHelp }) {
  const { view, staff, setStaff, t } = useApp();
  const nav = useNavigate();
  const shift = currentShift(view);
  const full = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.()).catch?.(() => {});
  return (
    <header className="lg:hidden sticky top-0 z-40 h-16 bg-[#0B2A5B] bg-[linear-gradient(90deg,#0F3C7A,#0B2A5B_60%,#061436)] text-white no-print border-b border-[#061436]" data-testid="tpv-terminal-bar">
      <div className="h-full px-2 sm:px-4 flex items-center gap-2">
        <span className="w-10 h-10 shrink-0 grid place-items-center"><Logo className="w-10 h-10" /></span>
        <div className="min-w-0 hidden min-[420px]:block">
          <div className="text-[10px] uppercase tracking-widest text-white/70">TPV · {t("app_name")}</div>
          <div className="font-heading truncate text-sm sm:text-base max-w-[180px] lg:max-w-xs" data-testid="header-shop-name">{view.shop?.name}</div>
        </div>
        <button type="button" onClick={onShift} data-testid="tpv-shift-pill"
          className={`ms-1 h-10 px-3 rounded border text-xs sm:text-sm font-bold flex items-center gap-1.5 whitespace-nowrap ${shift ? "bg-[#E6F4EA] border-[#9CD3AE] text-[#23714A]" : "bg-[#C7353B] border-[#962D22] text-white"}`}>
          <Clock3 className="w-4 h-4" />{shift ? `${t("shift")} ${new Date(shift.opened_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : t("no_shift")}
        </button>
        <div className="flex-1" />
        <Clock />
        <ClockButton testid="tpv-clock-btn" className="hidden sm:flex" />
        <div className="hidden sm:flex items-center gap-1.5">
          <IconBtn icon={Inbox} label={`${t("open_drawer")} (F9)`} onClick={openDrawerManual} testid="tpv-drawer-btn" />
          <IconBtn icon={MonitorSmartphone} label={`${t("customer_display")} (F10)`} onClick={openCustomerDisplay} testid="tpv-display-btn" />
          <IconBtn icon={ScanSearch} label={`${t("price_check")} (F12)`} onClick={() => nav("/price-check")} testid="tpv-price-check-btn" />
          <IconBtn icon={Maximize} label={t("fullscreen")} onClick={full} testid="tpv-fullscreen-btn" className="hidden lg:grid" />
          <IconBtn icon={Keyboard} label={`${t("shortcuts")} (F1)`} onClick={onHelp} testid="tpv-help-btn" className="hidden lg:grid" />
        </div>
        <NotificationBell />
        <LangSwitch compact />
        <button type="button" onClick={() => setStaff(null)} data-testid="header-lock-btn" aria-label={t("lock")}
          className="ak-btn flex items-center gap-1.5 h-10 px-3 rounded text-sm font-bold transition-colors">
          <Lock className="w-4 h-4" /><span className="hidden sm:inline max-w-24 truncate" data-testid="header-staff-name">{staff?.name}</span>
        </button>
      </div>
    </header>
  );
}

function FKeyBar({ onKey }) {
  const { t } = useApp();
  return (
    <div className="hidden lg:flex fixed bottom-0 inset-x-0 h-11 z-30 bg-[#061436] text-white/80 items-center gap-1 px-2 overflow-x-auto no-scrollbar no-print" data-testid="tpv-fkey-bar">
      {SHORTCUTS.filter(([k]) => k.startsWith("F")).map(([k, label]) => (
        <button key={k} type="button" onClick={() => onKey(k)} data-testid={`fkey-${k}`}
          className="h-8 px-2.5 rounded-md bg-white/5 hover:bg-white/15 flex items-center gap-1.5 text-xs whitespace-nowrap transition-colors">
          <kbd dir="ltr" className="font-bold text-[#FFB547]">{k}</kbd>{t(label)}
        </button>
      ))}
    </div>
  );
}

export default function AppShell() {
  const { can, setStaff, view, staff } = useApp();
  const nav = useNavigate();
  useEffect(() => {
    document.documentElement.classList.add("avalon-ui");
    return () => document.documentElement.classList.remove("avalon-ui");
  }, []);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftTab, setShiftTab] = useState("x");
  const [help, setHelp] = useState(false);
  const items = NAV.filter((n) => (!n.perm || can(n.perm)) && showFor(n, view.shop?.type, view.shop, staff?.role));
  useEffect(() => {
    const h = (e) => { setShiftTab(e.detail?.tab || "x"); setShiftOpen(true); };
    const apply = () => document.documentElement.classList.toggle("touch-mode", touchActive());
    apply();
    window.addEventListener("tpv:shift", h);
    window.addEventListener("tpv:device", apply);
    return () => { window.removeEventListener("tpv:shift", h); window.removeEventListener("tpv:device", apply); };
  }, []);
  const global = {
    F1: () => setHelp(true), F3: () => document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true })), F6: () => setShiftOpen(true), F7: () => setStaff(null),
    F9: openDrawerManual, F10: openCustomerDisplay, F4: () => closeAllWindows(), F12: () => nav("/price-check"),
    ...Object.fromEntries(items.slice(0, 9).map((n, i) => [`Alt+${i + 1}`, () => nav(n.to)])),
  };
  useHotkeys(global);
  const fireKey = (k) => (global[k] ? global[k]() : window.dispatchEvent(new KeyboardEvent("keydown", { key: k, bubbles: true })));
  return (
    <div className="min-h-screen pb-24 lg:pb-10 tpv-shell overflow-x-clip bg-[#F0F0F0]">
      <TerminalBar onShift={() => setShiftOpen(true)} onHelp={() => setHelp(true)} />
      <RibbonHeader />
      <PlanBanner />
      <main className="max-w-[1600px] lg:max-w-none mx-auto px-2 sm:px-4 lg:px-3 py-3 sm:py-4 lg:py-3"><Outlet /></main>
      <StatusBar />
      <BottomNav />
      <ShiftDialog open={shiftOpen} initialTab={shiftTab} onClose={() => setShiftOpen(false)} />
      <HelpDialog open={help} onClose={() => setHelp(false)} />
      <DrawerKick />
    </div>
  );
}
